'use client';
import type { FC } from 'react';
import { Suspense, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { SearchOutlined } from '@ant-design/icons';
import { SignedIn, SignedOut, SignInButton, useUser } from '@clerk/nextjs';
import { useDispatch, useSelector } from 'react-redux';
import { setLanguage } from '@/redux/reducers/languageSlice';
import type { RootState } from '@/redux/store';
import { T, useTolgee, useTranslate } from '@tolgee/react';

// Components
import CategoryNav from '../Navigation/CategoryNav';
import Loader from '../Loader/Loader';
import NavigationLinks from '../Navigation/NavigationLinks';
import Dropdown from '../Dropdown/Dropdown';
import SearchModal from '../Modal/SearchModal';
import BurgerMenuIcon from '../Icons/Navigation/BurgerMenuIcon';
import CloseIcon from '../Icons/Action/CloseIcon';
import MessageIcon from '../Icons/Navigation/MessageIcon';

// Config & Hooks
import { ROUTES } from '@/config/routes';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';
import { useCurrentUser } from '@/redux/selectors/userSelector';
import { useUserService } from '@/hooks/useUserService';
import { useUnreadMessagesCount } from '@/hooks/useUnreadMessagesCount';
import { useMessageRefresh } from '@/contexts/MessageRefreshContext';
import { canAccessAdmin } from '@/app/hoc/withAdminAuthorization';
import { normalizeLocale } from '@/utils/helpers';
import { getAppUrl } from '@/utils/clerkApp';

interface DefaultHeaderProps {
  showCategoryNav?: boolean;
}

const DefaultHeader: FC<DefaultHeaderProps> = ({ showCategoryNav = true }) => {
  // Hooks
  const { user } = useUser();
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const tolgee = useTolgee();
  const { updateLanguage } = useUserService();
  const userFromState = useCurrentUser();
  const language = useSelector((state: RootState) => state.language.language);
  const { count: unreadMessagesCount, refetch: refetchUnreadCount } = useUnreadMessagesCount();
  const { refreshTrigger } = useMessageRefresh();

  useEffect(() => {
    if (refreshTrigger > 0) {
      void refetchUnreadCount();
    }
  }, [refreshTrigger, refetchUnreadCount]);

  // State
  const [isUserDropdownVisible, setIsUserDropdownVisible] = useState(false);
  const [isLanguageDropdownVisible, setIsLanguageDropdownVisible] = useState(false);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Refs
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when search is open or mobile menu is open
  useBodyScrollLock(isSearchOpen || isMobileMenuVisible);

  // Event handlers
  const toggleUserDropdown = () => setIsUserDropdownVisible(!isUserDropdownVisible);
  const toggleMobileMenu = () => setIsMobileMenuVisible(!isMobileMenuVisible);
  const toggleLanguageDropdown = () => setIsLanguageDropdownVisible(!isLanguageDropdownVisible);

  const handleLanguageChange = (newLanguage: string) => {
    dispatch(setLanguage(newLanguage));
    void tolgee.changeLanguage(normalizeLocale(newLanguage));
    setIsLanguageDropdownVisible(false);
    void updateLanguage(userFromState.id, newLanguage);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownVisible(false);
      }
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate menu items
  const { role, hasOhsHubAccess } = useCurrentUser();
  const userMenuItems = [
    {
      key: '1',
      label: t('navigation.main.user.popover.booking_history.label'),
      href: ROUTES.BOOKING_HISTORY,
    },
    { key: '2', label: t('navigation.main.user.popover.settings.label'), href: ROUTES.SETTINGS },
    {
      key: '3',
      label: t('navigation.main.user.popover.help_center.label'),
      href: ROUTES.HELPCENTER,
    },
    { key: '4', label: t('navigation.main.user.popover.logout.label'), href: ROUTES.LOGOUT },
  ];

  // Add admin access if user has permission
  if (canAccessAdmin(role)) {
    userMenuItems.push({
      key: '5',
      label: t('navigation.main.user.popover.admin.label'),
      href: ROUTES.ADMIN,
    });
  }

  // Add ohs-hub access if user has permission
  if (hasOhsHubAccess) {
    userMenuItems.push({
      key: '6',
      label: t('navigation.main.user.popover.ohs-hub.label'),
      href: getAppUrl('ginione', '/ohs-hub'),
    });
  }

  const languageMenuItems = [
    {
      key: '1',
      label: t('navigation.main.language_select.popover.german.label'),
      displayCode: t('navigation.main.language_select.german.label'),
      onClick: () => handleLanguageChange('de-DE'),
    },
    {
      key: '2',
      label: t('navigation.main.language_select.popover.english.label'),
      displayCode: t('navigation.main.language_select.english.label'),
      onClick: () => handleLanguageChange('en'),
    },
    {
      key: '3',
      label: t('navigation.main.language_select.popover.chinese.label'),
      displayCode: t('navigation.main.language_select.chinese.label'),
      onClick: () => handleLanguageChange('zh-Hans'),
    },
  ];

  // Component for user menu dropdown
  const UserMenu = () => (
    <div ref={userDropdownRef} className="flex items-center">
      <Dropdown
        imageUrl={user?.imageUrl}
        fullName={user?.fullName || ''}
        menuItems={userMenuItems}
        isOpen={isUserDropdownVisible}
        onToggle={toggleUserDropdown}
        onClose={() => setIsUserDropdownVisible(false)}
      />
    </div>
  );

  // Component for language dropdown
  const LanguageMenu = () => {
    if (!language) return null;

    return (
      <div className="relative flex items-center" ref={languageDropdownRef}>
        <Dropdown
          label={language.slice(0, 2).toUpperCase()}
          menuItems={languageMenuItems}
          isOpen={isLanguageDropdownVisible}
          onToggle={toggleLanguageDropdown}
          onClose={() => setIsLanguageDropdownVisible(false)}
          showIcon={false}
        />
      </div>
    );
  };

  return (
    <>
      {/* Main Header */}
      <header className="default-header flex items-center justify-between p-4 bg-white border-b md:border-b-0 xl:border-b border-gray-300 h-[72px]">
        {/* Left side - Logo and Navigation */}
        <div className="flex items-center">
          <Link href="/" passHref prefetch={true}>
            <div className="pl-3.5">
              <Image
                src="/bloom-full-logo-black.svg"
                alt="Logo"
                width={100}
                height={50}
                className="w-25"
              />
            </div>
          </Link>
          <div className="hidden xl:block">
            <NavigationLinks />
          </div>
          <div className="flex md:hidden ml-8 cursor-pointer" onClick={toggleMobileMenu}>
            <BurgerMenuIcon />
          </div>
        </div>

        {/* Right side - User Actions */}
        <div className="flex items-center h-full">
          <SignedOut>
            <SignInButton mode="modal" />
          </SignedOut>

          <SignedIn>
            {/* Language Selector */}
            {language && (
              <div className="hidden md:block mx-3">
                <LanguageMenu />
              </div>
            )}
            {/* Acute Help Button */}
            <div className="hidden xl:block mx-3">
              <Link
                href={ROUTES.ACUTE_HELP}
                className="rounded-full py-2.5 px-4 text-lg bg-blue-200 inline-block"
                passHref
                prefetch={true}
              >
                <T>navigation.main.acute_help.label</T>
              </Link>
            </div>
            <div className="flex items-center h-full">
              {/* Icons container - ensures both icons are the same height and aligned */}
              <div className="flex items-center h-full">
                {/* Messages Icon */}
                <div className="px-3 flex items-center h-full">
                  <Link
                    href={ROUTES.MESSAGES}
                    className="flex items-center h-full text-xl translate-y-0 hover:-translate-y-0.5 transition-transform duration-300"
                  >
                    <MessageIcon count={unreadMessagesCount} />
                  </Link>
                </div>

                {/* Search Icon */}
                <div className="px-3 flex items-center h-full">
                  <span
                    onClick={() => setIsSearchOpen(true)}
                    className="flex items-center h-full cursor-pointer translate-y-0 hover:-translate-y-0.5 transition-transform duration-300"
                  >
                    <SearchOutlined className="text-black text-xl" />
                  </span>
                </div>
              </div>

              {/* User Menu */}
              <div>
                <UserMenu />
              </div>
            </div>
          </SignedIn>
        </div>
      </header>

      {/* Tablet Navigation */}
      <div className="hidden md:block xl:hidden border-b border-gray-300 pb-4">
        <NavigationLinks />
      </div>

      {/* Category Navigation */}
      {showCategoryNav && (
        <Suspense fallback={<Loader position={'absolute'} />}>
          <CategoryNav />
        </Suspense>
      )}

      {/* Mobile Menu */}
      {isMobileMenuVisible && (
        <div className="fixed inset-0 z-50 bg-gray-100 p-5 flex flex-col items-start md:hidden h-full overflow-y-auto">
          {/* Mobile Menu Header */}
          <div className="flex justify-between items-center w-full mb-4">
            <Image
              src="/bloom-full-logo-black.svg"
              alt="Logo"
              width={100}
              height={32}
              style={{ width: 'auto', height: 'auto' }}
            />
            <div onClick={toggleMobileMenu} className="cursor-pointer">
              <CloseIcon />
            </div>
          </div>

          {/* Mobile Menu User Section */}
          <div className="flex justify-between items-center w-full mb-5 mt-12">
            <UserMenu />
            <div className="flex items-center">
              <div className="px-3">
                <SearchOutlined
                  onClick={() => setIsSearchOpen(true)}
                  className="text-black text-xl cursor-pointer mr-2"
                />
              </div>
              <LanguageMenu />
            </div>
          </div>

          {/* Mobile Navigation Links */}
          <div className="bg-white p-8 w-full rounded-xl mb-5">
            <NavigationLinks isMobile={true} onLinkClick={toggleMobileMenu} />
            <div className="mt-6 w-full border-t border-gray-300 pt-5">
              <nav className="flex flex-col gap-2 text-lg">
                {userMenuItems.slice(0, 4).map((item) => (
                  <Link
                    key={item.key}
                    href={item.href}
                    onClick={toggleMobileMenu}
                    className="py-2 text-base"
                    passHref
                    prefetch={true}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Mobile Acute Help Button */}
          <Link
            href={ROUTES.ACUTE_HELP}
            onClick={toggleMobileMenu}
            className="block w-full text-center py-2.5 mb-4 text-lg bg-blue-200 rounded-full"
          >
            <T>navigation.main.acute_help.label</T>
          </Link>
        </div>
      )}

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default DefaultHeader;
